import { createFileRoute } from "@tanstack/react-router";
import {
  Check,
  Zap,
  Star,
  Users,
  ChevronDown,
  Building2,
  Swords,
  Info,
} from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";

export const Route = createFileRoute("/(public)/pricing")({
  staticData: { title: "Pricing" },
  component: PricingPage,
});

type BillingCycle = "monthly" | "yearly";

function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("yearly");

  return (
    <div className="min-h-screen bg-surface-950 text-white selection:bg-brand-500/30">
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative pt-32 pb-12 px-6">
          {/* Cyberpunk Background Effect - scoped to hero */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {/* Grid */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
            {/* Glow */}
            <div className="absolute top-30 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[300px] bg-brand-500/10 blur-[100px] rounded-full mix-blend-screen" />
            {/* CRT Scanline Overlay (Optional retro feel) */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.1)_50%),linear-gradient(90deg,rgba(255,0,0,0.03),rgba(0,255,0,0.01),rgba(0,0,255,0.03))] z-50 pointer-events-none bg-[size:100%_3px,3px_100%]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-bold tracking-wider uppercase mb-6"
            >
              <Zap className="size-3 fill-current" />
              Early Access Pricing
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              className="font-display text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]"
            >
              LEVEL UP YOUR CLUB
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-surface-400 text-lg md:text-xl max-w-2xl mx-auto mb-6 leading-relaxed"
            >
              Jump in for free. Upgrade when you want more control and advanced
              features. Transparent pricing. Built for competition.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="flex justify-center mb-8"
            >
              <div className="inline-flex items-center gap-2 px-3 py-2 md:px-5 md:py-2.5 bg-slate-500/15 rounded-sm backdrop-blur-sm shadow-2xl">
                <Info className="size-4 text-brand-400" />
                <span className="text-xs md:text-sm font-bold text-brand-400 uppercase tracking-wide md:tracking-widest">
                  Upgrade from your club page
                </span>
              </div>
            </motion.div>

            {/* Optimized Billing Toggle (Segmented Control) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex justify-center mb-8 md:mb-16"
            >
              <div className="relative inline-flex bg-surface-900/80 p-0.5 rounded-full border border-surface-700 backdrop-blur-md ring-1 ring-white/5">
                {/* Sliding Background Pill */}
                <div
                  className={`absolute top-0.5 bottom-0.5 left-0.5 w-[110px] rounded-full bg-surface-700 shadow-inner border border-white/5 transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] ${
                    billingCycle === "yearly"
                      ? "translate-x-[110px]"
                      : "translate-x-0"
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-full" />
                </div>

                {/* Monthly Button */}
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`relative w-[110px] py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer ${
                    billingCycle === "monthly"
                      ? "text-white"
                      : "text-surface-400 hover:text-surface-200"
                  }`}
                >
                  Monthly
                </button>

                {/* Yearly Button */}
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`relative w-[110px] py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer flex items-center justify-center gap-1.5 ${
                    billingCycle === "yearly"
                      ? "text-white"
                      : "text-surface-400 hover:text-surface-200"
                  }`}
                >
                  Yearly
                  {/* Badge */}
                  <span
                    className={`px-1 py-0.5 text-[9px] rounded font-black transition-all duration-300 ${
                      billingCycle === "yearly"
                        ? "bg-brand-500 text-surface-950 shadow-[0_0_10px_rgba(38,217,181,0.5)]"
                        : "bg-brand-500/20 text-brand-400"
                    }`}
                  >
                    -25%
                  </span>
                </button>
              </div>
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
              {/* Casual Tier */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="group relative bg-surface-950 backdrop-blur-md border border-surface-600 rounded-3xl p-5 flex flex-col transition-colors hover:bg-surface-900"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-surface-500/20 rounded-xl border border-surface-500/40">
                    <Users className="size-6 text-surface-300" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-display text-2xl font-bold text-white">
                      CASUAL
                    </h3>
                    <p className="text-sm text-surface-400">
                      Standard tier for all clubs
                    </p>
                  </div>
                </div>

                <div className="mb-6 text-left">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-5xl font-bold text-white">
                      $0
                    </span>
                    <span className="text-surface-500">/ forever</span>
                  </div>
                  <p className="text-surface-400 text-sm mt-2">
                    Core features included
                  </p>
                </div>

                <div className="h-px w-full bg-surface-600 mb-6" />

                <ul className="space-y-4 text-left">
                  <Feature>Unlimited games</Feature>
                  <Feature>Max 2 match queues total</Feature>
                  <Feature>Basic match flow</Feature>
                  <Feature>Leaderboards</Feature>
                </ul>
              </motion.div>

              {/* Competitive Tier */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="relative group"
              >
                {/* Glow Effect behind card */}
                <div className="absolute -inset-[1px] bg-gradient-to-b from-brand-400 to-transparent rounded-3xl opacity-50 blur-sm group-hover:opacity-100 transition duration-500" />

                <div className="relative bg-surface-900/90 backdrop-blur-xl border border-brand-500/50 rounded-3xl p-5 flex flex-col h-full shadow-[0_0_50px_-12px_rgba(38,217,181,0.25)] md:scale-105 z-10">
                  {/* Floating Badge */}
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-brand-600 to-brand-400 text-surface-950 font-black text-xs uppercase tracking-widest rounded-full shadow-[0_4px_12px_rgba(38,217,181,0.4)] flex items-center gap-1.5">
                    <Star className="size-4 fill-current" />
                    Recommended
                  </div>

                  <div className="flex items-center gap-4 mb-6 pt-2">
                    <div className="p-3 bg-brand-500/20 rounded-xl border border-brand-500/20">
                      <Swords className="size-6 text-brand-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-display text-2xl font-bold text-white">
                        COMPETITIVE
                      </h3>
                      <p className="text-sm text-brand-400">
                        For communities that need customization
                      </p>
                    </div>
                  </div>

                  <div className="mb-6 text-left">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-5xl font-bold text-white">
                        ${billingCycle === "monthly" ? "20" : "15"}
                      </span>
                      <span className="text-surface-400">/ mo</span>
                    </div>
                    <p className="text-sm mt-2 text-surface-400">
                      {billingCycle === "yearly" ? (
                        <span className="text-brand-400 font-bold">
                          Billed $180 yearly (Save $60)
                        </span>
                      ) : (
                        "Billed monthly"
                      )}
                    </p>
                  </div>

                  <div className="h-px w-full bg-gradient-to-r from-transparent via-brand-500/50 to-transparent mb-6" />

                  <ul className="space-y-4 text-left">
                    <Feature highlighted>Everything in Casual</Feature>
                    <Feature highlighted>Unlimited queues during beta</Feature>
                    <Feature highlighted>
                      Enhanced visual match editor with powerful customization
                    </Feature>
                    <Feature highlighted>Advanced queue/match settings</Feature>
                    <Feature highlighted>Season management</Feature>
                  </ul>
                </div>
              </motion.div>

              {/* Organization Tier */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="relative group"
              >
                {/* Glow Effect behind card */}
                <div className="absolute -inset-[1px] bg-gradient-to-b from-purple-400 to-transparent rounded-3xl opacity-40  group-hover:opacity-80 transition duration-500" />

                <div className="relative bg-surface-900/90 backdrop-blur-xl border border-purple-500/40 rounded-3xl p-5 flex flex-col h-full ">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/20">
                      <Building2 className="size-6 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-display text-2xl font-bold text-white">
                        ORGANIZATION
                      </h3>
                      <p className="text-sm text-purple-400">
                        For large-scale eSports organizers
                      </p>
                    </div>
                  </div>

                  <div className="mb-6 text-left">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-4xl font-bold text-white">
                        Contact us
                      </span>
                    </div>
                    <p className="text-surface-400 text-sm mt-2">
                      Custom pricing available
                    </p>
                  </div>

                  <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-500/50 to-transparent mb-6" />

                  <ul className="space-y-4 text-left">
                    <Feature highlighted variant="purple">
                      Everything in Competitive
                    </Feature>
                    <Feature highlighted variant="purple">
                      API access & webhooks
                    </Feature>
                    <Feature highlighted variant="purple">
                      Dedicated support
                    </Feature>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="py-10 px-6 bg-surface-950/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-4">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="font-display text-4xl md:text-5xl font-bold text-white mb-1"
              >
                COMPARE TIERS
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05, duration: 0.5 }}
                className="text-surface-400 text-lg"
              >
                Choose the perfect tier for your competitive needs
              </motion.p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-surface-600/50">
                    <th></th>
                    <th className="py-4 px-6 text-center">
                      <div className="font-display text-xl font-bold text-white">
                        CASUAL
                      </div>
                      <div className="text-sm text-surface-400 mt-1">Free</div>
                    </th>
                    <th className="py-4 px-6 text-center bg-brand-500/5">
                      <div className="font-display text-xl font-bold text-white">
                        COMPETITIVE
                      </div>
                      <div className="text-sm text-brand-400 mt-1 font-bold">
                        ${billingCycle === "monthly" ? "20" : "15"}/mo
                      </div>
                    </th>
                    <th className="py-4 px-6 text-center bg-purple-500/5">
                      <div className="font-display text-xl font-bold text-white">
                        ORGANIZATION
                      </div>
                      <div className="text-sm text-purple-400 mt-1 font-bold">
                        Contact us
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow
                    feature="Match Queues"
                    casual="Max 2 total"
                    competitive="Unlimited"
                    organization="Unlimited"
                  />
                  <ComparisonRow
                    feature="Match Flow"
                    casual="Basic"
                    competitive="Enhanced visual editor"
                    organization="Enhanced visual editor"
                  />
                  <ComparisonRow
                    feature="Queue/Match Settings"
                    casual={false}
                    competitive="Advanced"
                    organization="Advanced"
                  />
                  <ComparisonRow
                    feature="Season Management"
                    casual={false}
                    competitive={true}
                    organization={true}
                  />
                  <ComparisonRow
                    feature="Leaderboards"
                    casual={true}
                    competitive={true}
                    organization={true}
                  />
                  <ComparisonRow
                    feature="API & Webhooks"
                    casual={false}
                    competitive={false}
                    organization={true}
                  />
                  <ComparisonRow
                    feature="Support"
                    casual="Community"
                    competitive="Priority"
                    organization="Dedicated"
                  />
                </tbody>
              </table>
            </div>

            {/* Mobile Accordion */}
            <div className="md:hidden space-y-4">
              <MobileTierComparison
                tier="CASUAL"
                price="Free"
                features={{
                  "Match Queues": "Max 2 total",
                  "Match Flow": "Basic",
                  "Queue/Match Settings": false,
                  "Season Management": false,
                  Leaderboards: true,
                  "API & Webhooks": false,
                  Support: "Community",
                }}
              />
              <MobileTierComparison
                tier="COMPETITIVE"
                price={`$${billingCycle === "monthly" ? "20" : "15"}/mo`}
                highlight="brand"
                features={{
                  "Match Queues": "Unlimited",
                  "Match Flow": "Enhanced visual editor",
                  "Queue/Match Settings": "Advanced",
                  "Season Management": true,
                  Leaderboards: true,
                  "API & Webhooks": false,
                  Support: "Priority",
                }}
              />
              <MobileTierComparison
                tier="ORGANIZATION"
                price="Contact us"
                highlight="purple"
                features={{
                  "Match Queues": "Unlimited",
                  "Match Flow": "Enhanced visual editor",
                  "Queue/Match Settings": "Advanced",
                  "Season Management": true,
                  Leaderboards: true,
                  "API & Webhooks": true,
                  Support: "Dedicated",
                }}
              />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-7 px-6 bg-surface-950/50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-5">
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05, duration: 0.5 }}
                className="font-display text-3xl md:text-4xl font-bold text-white"
              >
                FAQs
              </motion.h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="space-y-4"
            >
              <FAQItem
                question="Can I upgrade or downgrade anytime?"
                answer="Yes! You can upgrade at any time and get instant access. Downgrades take effect at the end of your billing period, but you can continue using premium features until then."
              />
              <FAQItem
                question="How many clubs can I create and manage?"
                answer="As many as you want! Each club requires its own subscription, so you can manage multiple competitive communities under one account."
              />
              <FAQItem
                question="Do you offer refunds?"
                answer="We do not offer refunds for completed payments, but if you encounter any issues, please contact our support team and we'll do our best to assist you."
              />
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------
// Helper Components
// ---------------------------

function Feature({
  children,
  highlighted = false,
  dimmed = false,
  variant = "brand",
}: {
  children: React.ReactNode;
  highlighted?: boolean;
  dimmed?: boolean;
  variant?: "brand" | "purple";
}) {
  const checkboxStyles = highlighted
    ? variant === "purple"
      ? "bg-purple-500/20 text-purple-400 group-hover/item:text-purple-300"
      : "bg-brand-500/20 text-brand-400 group-hover/item:text-brand-300"
    : dimmed
      ? "text-surface-500"
      : "bg-surface-700 text-surface-300 group-hover/item:text-white";

  return (
    <li className="flex items-start gap-3 group/item">
      <div
        className={`flex-shrink-0 mt-0.5 p-0.5 rounded-full ${checkboxStyles} transition-colors`}
      >
        <Check className="size-3.5 stroke-[3]" />
      </div>
      <span
        className={`text-sm transition-colors ${
          highlighted
            ? "text-white font-medium"
            : dimmed
              ? "text-surface-500 line-through"
              : "text-surface-200 group-hover/item:text-surface-100"
        }`}
      >
        {children}
      </span>
    </li>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={`border overflow-hidden transition-all duration-300 rounded-md ${
        isOpen
          ? "bg-surface-800 border-surface-700"
          : "bg-surface-900/30 border-surface-700 hover:border-surface-600"
      }`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left focus:outline-none cursor-pointer"
      >
        <span className={`font-bold transition-colors`}>{question}</span>
        <ChevronDown
          className={`size-5 text-surface-500 transition-transform duration-300 ${isOpen ? "rotate-180 text-brand-400" : ""}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-5 text-surface-300 text-sm leading-relaxed">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
}

function ComparisonRow({
  feature,
  casual,
  competitive,
  organization,
}: {
  feature: string;
  casual: string | boolean;
  competitive: string | boolean;
  organization: string | boolean;
}) {
  const renderCell = (
    value: string | boolean,
    highlight?: "brand" | "purple",
  ) => {
    if (typeof value === "boolean") {
      return (
        <div className="flex items-center justify-center">
          {value ? (
            <Check
              className={`size-5 ${highlight === "purple" ? "text-purple-400" : highlight === "brand" ? "text-brand-400" : "text-surface-400"}`}
            />
          ) : (
            <span className="text-surface-600">—</span>
          )}
        </div>
      );
    }
    return (
      <span
        className={`text-sm ${highlight === "purple" ? "text-purple-300" : highlight === "brand" ? "text-brand-300" : "text-surface-300"}`}
      >
        {value}
      </span>
    );
  };

  return (
    <tr className="border-b border-surface-700/60 transition-colors">
      <td className="py-4 px-6 text-surface-200 font-medium">{feature}</td>
      <td className="py-4 px-6 text-center">{renderCell(casual)}</td>
      <td className="py-4 px-6 text-center bg-brand-500/5">
        {renderCell(competitive, "brand")}
      </td>
      <td className="py-4 px-6 text-center bg-purple-500/5">
        {renderCell(organization, "purple")}
      </td>
    </tr>
  );
}

function MobileTierComparison({
  tier,
  price,
  features,
  highlight,
}: {
  tier: string;
  price: string;
  features: Record<string, string | boolean>;
  highlight?: "brand" | "purple";
}) {
  const [isOpen, setIsOpen] = useState(false);
  const bgColor =
    highlight === "brand"
      ? "bg-brand-500/5"
      : highlight === "purple"
        ? "bg-purple-500/5"
        : "bg-surface-800/30";
  const borderColor =
    highlight === "brand"
      ? "border-brand-500/30"
      : highlight === "purple"
        ? "border-purple-500/30"
        : "border-surface-700";
  const textColor =
    highlight === "brand"
      ? "text-brand-400"
      : highlight === "purple"
        ? "text-purple-400"
        : "text-white";

  return (
    <div
      className={`border ${borderColor} rounded-xl overflow-hidden ${bgColor}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between"
      >
        <div className="text-left">
          <div className={`font-display text-xl font-bold ${textColor}`}>
            {tier}
          </div>
          <div className="text-sm text-surface-400 mt-1">{price}</div>
        </div>
        <ChevronDown
          className={`size-5 text-surface-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div
            className={`px-6 pb-4 border-t pt-4 space-y-3 ${
              highlight === "brand"
                ? "border-brand-500/20"
                : highlight === "purple"
                  ? "border-purple-500/20"
                  : "border-surface-700"
            }`}
          >
            {Object.entries(features).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-surface-400">{key}</span>
                {typeof value === "boolean" ? (
                  value ? (
                    <Check
                      className={`size-4 ${highlight === "purple" ? "text-purple-400" : highlight === "brand" ? "text-brand-400" : "text-surface-400"}`}
                    />
                  ) : (
                    <span className="text-surface-600">—</span>
                  )
                ) : (
                  <span
                    className={`text-sm font-medium ${highlight === "purple" ? "text-purple-300" : highlight === "brand" ? "text-brand-300" : "text-surface-200"}`}
                  >
                    {value}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
